import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { WithdrawFundsDialog } from 'src/components/wallet/withdraw-funds-dialog';
import { MainWalletTransactionHistoryDialog } from 'src/components/wallet/main-wallet-transaction-history-dialog';
import {
  MainWalletPurpose,
  MainWalletTransactionType,
  useGetMainWalletBalancesByPurposeQuery,
  useGetOrganizationQuery,
  useGetWalletsMainQuery,
} from 'src/generated/graphql';
import { varAlpha } from 'src/theme/styles';
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const f = (v: string | null | undefined) => parseFloat(v ?? '0') || 0;

const truncate = (a: string) => (a.length > 20 ? `${a.slice(0, 12)}...${a.slice(-6)}` : a);

const PURPOSE_LABELS: Record<MainWalletPurpose, string> = {
  [MainWalletPurpose.Default]: 'Custodial',
  [MainWalletPurpose.CheddarPay]: 'Checkout',
  [MainWalletPurpose.BatchPayout]: 'Batch Payout',
};

// ----------------------------------------------------------------------

type MainWalletRow = { chain?: string | null; address: string; isTestnet?: boolean | null };

type NavWalletBalanceWidgetProps = {
  organizationId: string;
  compact?: boolean;
};

export function NavWalletBalanceWidget({ organizationId, compact }: NavWalletBalanceWidgetProps) {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [miniAnchor, setMiniAnchor] = useState<null | HTMLElement>(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [depositHistoryOpen, setDepositHistoryOpen] = useState(false);
  const [withdrawHistoryOpen, setWithdrawHistoryOpen] = useState(false);
  // Collapsed by default: the widget sits outside the nav Scrollbar, so an
  // expanded list would squeeze the nav items rather than scroll.
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const menuFromBig = Boolean(menuAnchor);
  const menuFromMini = Boolean(miniAnchor);

  const [{ data: organizationData }] = useGetOrganizationQuery({
    variables: { organizationId },
    pause: !organizationId,
  });
  const canViewBilling = organizationData?.getOrganization?.canViewBilling ?? false;

  // Read-only totals per purpose. Deliberately not getWallets: that resolver
  // provisions missing main wallets as a side effect, which must not happen
  // from the nav on every page load.
  const [{ data: balancesData, fetching: walletsFetching }, refetchBalances] =
    useGetMainWalletBalancesByPurposeQuery({ variables: { organizationId } });

  const [{ data: mainWalletsData, fetching: mainFetching }, refetchMain] = useGetWalletsMainQuery({
    variables: {
      organizationId,
      searchQuery: null,
      purpose: MainWalletPurpose.Default,
    },
  });

  const mainWallets = useMemo(
    () => (mainWalletsData?.getWalletsMain ?? []).filter((w) => !w.isTestnet),
    [mainWalletsData?.getWalletsMain]
  );

  // The API omits purposes the org has no wallets for, so an org without Batch
  // Payout or Checkout never sees a "$0.00" line for it.
  const breakdown = useMemo(
    () =>
      (balancesData?.getMainWalletBalancesByPurpose ?? []).map((row) => ({
        key: row.purpose,
        label: PURPOSE_LABELS[row.purpose],
        usd: f(row.totalBalanceUSD),
      })),
    [balancesData?.getMainWalletBalancesByPurpose]
  );

  const totalUsd = useMemo(() => breakdown.reduce((s, row) => s + row.usd, 0), [breakdown]);

  const onRefresh = useCallback(() => {
    void refetchBalances({ requestPolicy: 'network-only' });
    void refetchMain({ requestPolicy: 'network-only' });
  }, [refetchBalances, refetchMain]);

  const handleCopy = useCallback(async (addr: string) => {
    try {
      await navigator.clipboard.writeText(addr);
      toast.success('Address copied');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Copy failed';
      toast.error(message);
    }
  }, []);

  const closeMainMenus = () => {
    setMenuAnchor(null);
    setMiniAnchor(null);
  };

  const allItems = [
    {
      key: 'deposit' as const,
      label: 'Deposit',
      sub: 'Main wallet addresses',
      icon: 'solar:add-square-bold' as const,
      onSelect: () => {
        closeMainMenus();
        onRefresh();
        setDepositOpen(true);
      },
    },
    {
      key: 'withdraw' as const,
      label: 'Withdraw',
      sub: 'To whitelisted address',
      icon: 'solar:export-bold' as const,
      onSelect: () => {
        closeMainMenus();
        onRefresh();
        setWithdrawOpen(true);
      },
    },
    {
      key: 'depHist' as const,
      label: 'Deposit history',
      sub: 'Main wallet deposits',
      icon: 'solar:history-bold' as const,
      onSelect: () => {
        closeMainMenus();
        setDepositHistoryOpen(true);
      },
    },
    {
      key: 'wHist' as const,
      label: 'Withdrawal history',
      sub: 'Main wallet withdrawals',
      icon: 'solar:clock-circle-bold' as const,
      onSelect: () => {
        closeMainMenus();
        setWithdrawHistoryOpen(true);
      },
    },
    {
      key: 'transfer' as const,
      label: 'Transfer between wallets',
      sub: 'Move funds between wallet purposes',
      icon: 'solar:transfer-vertical-linear' as const,
      onSelect: () => {
        closeMainMenus();
        navigate(paths.dashboard.transfer);
      },
    },
    {
      key: 'billing' as const,
      label: 'Billing',
      sub: 'Invoices and payments',
      icon: 'solar:wallet-money-linear' as const,
      onSelect: () => {
        closeMainMenus();
        navigate(paths.dashboard.billing.root);
      },
    },
  ] as const;

  // Billing is only offered to members with billing access in this org.
  const items = canViewBilling ? allItems : allItems.filter((item) => item.key !== 'billing');

  const bigMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={menuFromBig}
      onClose={() => setMenuAnchor(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      {items.map((item) => (
        <MenuItem key={item.key} onClick={item.onSelect} sx={{ py: 1.25, minWidth: 220 }}>
          <ListItemIcon>
            <Iconify icon={item.icon} width={22} />
          </ListItemIcon>
          <ListItemText primary={item.label} secondary={item.sub} />
        </MenuItem>
      ))}
    </Menu>
  );

  const breakdownRows = (
    <Stack spacing={0.5} sx={{ pt: 1 }}>
      {breakdown.map((row) => (
        <Stack key={row.key} direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="caption"
            sx={{ color: 'var(--layout-nav-text-disabled-color, text.secondary)' }}
          >
            {row.label}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {fmtUSD(row.usd)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  const smallMenu = (
    <Popover
      open={menuFromMini}
      anchorEl={miniAnchor}
      onClose={() => setMiniAnchor(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 1, minWidth: 200 }}>
        {breakdown.length > 0 && (
          <>
            <Box sx={{ px: 1.5, pb: 1 }}>{breakdownRows}</Box>
            <Divider sx={{ mb: 1 }} />
          </>
        )}
        {items.map((item) => (
          <MenuItem
            key={item.key}
            onClick={item.onSelect}
            dense
            sx={{ borderRadius: 1, py: 1 }}
          >
            <ListItemIcon>
              <Iconify icon={item.icon} width={20} />
            </ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        ))}
      </Box>
    </Popover>
  );

  const dialogs = (
    <>
      <MainWalletAddressesDialog
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        loading={mainFetching}
        wallets={mainWallets}
        onCopy={handleCopy}
      />
      <WithdrawFundsDialog
        organizationId={organizationId}
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onSuccess={onRefresh}
      />
      <MainWalletTransactionHistoryDialog
        organizationId={organizationId}
        open={depositHistoryOpen}
        onClose={() => setDepositHistoryOpen(false)}
        title="Deposit history (main wallet)"
        filterType={MainWalletTransactionType.Deposit}
      />
      <MainWalletTransactionHistoryDialog
        organizationId={organizationId}
        open={withdrawHistoryOpen}
        onClose={() => setWithdrawHistoryOpen(false)}
        title="Withdrawal history (main wallet)"
        filterType={MainWalletTransactionType.MainWithdrawal}
      />
    </>
  );

  const actionButton = (getAnchor: (el: HTMLElement) => void) => (
    <IconButton
      onClick={(e) => getAnchor(e.currentTarget)}
      size="small"
      color="primary"
      sx={(theme) => ({
        borderRadius: 1.5,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        flexShrink: 0,
        '&:hover': { bgcolor: 'primary.dark' },
        border: `1px solid ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)}`,
      })}
      aria-label="Wallet actions"
    >
      <Iconify icon="solar:round-transfer-diagonal-linear" width={22} />
    </IconButton>
  );

  if (compact) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
            py: 1,
            px: 0.5,
            borderTop: (t) => `1px solid ${t.vars.palette.divider}`,
            gap: 0.5,
          }}
        >
          <Box sx={{ textAlign: 'center', minWidth: 0, maxWidth: 1 }}>
            <Typography
              variant="caption"
              noWrap
              sx={{
                maxWidth: 1,
                color: 'var(--layout-nav-text-primary-color, text.primary)',
                fontWeight: 700,
              }}
            >
              {walletsFetching ? '…' : fmtUSD(totalUsd)}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              sx={{ color: 'var(--layout-nav-text-disabled-color, text.secondary)', fontSize: '0.65rem' }}
            >
              Wallet
            </Typography>
          </Box>
          {actionButton((el) => setMiniAnchor(el))}
        </Box>
        {smallMenu}
        {dialogs}
      </>
    );
  }

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          flexShrink: 0,
          mx: 2,
          mb: 1.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: (t) => varAlpha(t.vars.palette.grey['500Channel'], 0.04),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0}>
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {walletsFetching ? '…' : fmtUSD(totalUsd)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'var(--layout-nav-text-disabled-color, text.secondary)' }}
            >
              Wallet balance
            </Typography>
          </Box>
          {breakdown.length > 0 && (
            <IconButton
              size="small"
              onClick={() => setBreakdownOpen((open) => !open)}
              aria-label={breakdownOpen ? 'Hide balance breakdown' : 'Show balance breakdown'}
              aria-expanded={breakdownOpen}
              sx={{ flexShrink: 0 }}
            >
              <Iconify
                icon={breakdownOpen ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                width={18}
              />
            </IconButton>
          )}
          <Divider flexItem orientation="vertical" sx={{ height: 40, mx: 0.75 }} />
          {actionButton((el) => setMenuAnchor(el))}
        </Stack>

        <Collapse in={breakdownOpen} timeout="auto" unmountOnExit>
          {breakdownRows}
        </Collapse>
      </Card>
      {bigMenu}
      {dialogs}
    </>
  );
}

// ----------------------------------------------------------------------

function MainWalletAddressesDialog({
  open,
  onClose,
  loading,
  wallets,
  onCopy,
}: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  wallets: MainWalletRow[];
  onCopy: (a: string) => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Deposit to main wallet</DialogTitle>
      <DialogContent>
        {loading && (
          <Stack spacing={1.5} sx={{ py: 1 }}>
            <Skeleton height={48} />
            <Skeleton height={48} />
          </Stack>
        )}
        {!loading && wallets.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            No main wallet addresses found.
          </Typography>
        )}
        {!loading && (
        <Stack spacing={1.5} sx={{ py: 1 }}>
          {wallets.map((w) => (
            <Card key={`${w.chain}-${w.address}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ flexWrap: 'wrap' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">{w.chain ?? 'Chain'}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap title={w.address}>
                    {truncate(w.address)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onCopy(w.address)}
                  color="primary"
                  aria-label="Copy address"
                >
                  <Iconify icon="solar:copy-bold" width={20} />
                </IconButton>
              </Stack>
            </Card>
          ))}
        </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
